import { SolvedFieldsRatioLabel } from 'components/common-form/builder/form/node/field/SolvedFieldsRatioLabel';
import { ShareButtonNext } from 'components/common-form/sidePanel/docToolbar/summary';
import { FxSpot } from 'components/common-form/sidePanel/status/fxSpotUtils';
import { useClassNameFromTradeWay } from 'components/common-form/sidePanel/status/useClassNameFromTradeway';
import { CountdownTimer } from 'components/shared/CountdownTimer/CountdownTimer';
import { Price } from 'components/shared/Price/Price';
import {
  CircularCountdown,
  ConnectedTradeWayLiteral,
  FlowWayLiteral,
  PriceNext,
  QuoteStatus,
  QuoteYield,
  SolveResult,
} from 'components/shared/PriceNext';
import { ProgressBar } from 'components/shared/ProgressBar/ProgressBar';
import { useProgressTimer } from 'hooks/useProgressTimer';
import type { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { getFlowWayTranslationKey } from '../cashFlowWayUtils';
import { SolveModeLabel } from '../SolveMode';
import type { QuotedState } from './Quoted.Tradable.connect';
import { connectQuotedTradeable } from './Quoted.Tradable.connect';

export function Component({
  tabId,
  solveResult,
  solveResultFormat,
  tradeAuthStartTimeInMillis,
  tradeAuthDurationInMillis,
  solvingMode,
  isMultipleSolvingMode,
  children,
  workspaceType,
  flowWay,
  fxSpot,
  priceInPercent = 0,
  priceInCurrency = 0,
  currency = '',
  isExecOnlyAddon,
  quoteYield,
}: PropsWithChildren<QuotedState>) {
  const isDynamicWorkspace = workspaceType === 'dynamic';
  const { formatMessage } = useIntl();
  const { progressInPercent, remainingTimeInSeconds } = useProgressTimer(
    tradeAuthStartTimeInMillis,
    tradeAuthDurationInMillis,
  );
  const hasProgress = tradeAuthStartTimeInMillis !== 0 && tradeAuthDurationInMillis !== 0;
  const className = useClassNameFromTradeWay(tabId, workspaceType);

  if (workspaceType === 'static' || workspaceType === 'dynamic') {
    return (
      <PriceNext
        className={className}
        tradeWay={<ConnectedTradeWayLiteral tabId={tabId} workspaceType={workspaceType} />}
        solveMode={
          <SolveModeLabel
            workspaceType={workspaceType}
            solvingMode={solvingMode}
            isMultipleSolvingMode={isMultipleSolvingMode}
          />
        }
        shareButton={!isDynamicWorkspace && <ShareButtonNext className={className} tabId={tabId} />}
        solveResult={<SolveResult price={solveResult} priceFormat={solveResultFormat} />}
        fxSpot={<FxSpot greekLine={fxSpot} />}
        quoteYield={<QuoteYield yieldValue={quoteYield} />}
        solveRatio={<SolvedFieldsRatioLabel className="fw-extrabold align-middle" e2e="solving-fields-ratio" />}
        flowWay={
          !isExecOnlyAddon && (
            <FlowWayLiteral
              flowWay={flowWay}
              priceInPercent={priceInPercent}
              priceInCurrency={priceInCurrency}
              currency={currency}
            />
          )
        }
        countDownBar={<ProgressBar className="bg-secondary" percent={100 - progressInPercent} />}
        quoteStatus={
          <QuoteStatus
            icon={CircularCountdown}
            description="sidePanel.quoteStatus.tradableFor"
            remainingTimeInSeconds={remainingTimeInSeconds}
          />
        }
      />
    );
  }

  return (
    <>
      <Price
        color="info"
        price={solveResult}
        priceFormat={solveResultFormat}
        fxSpot={fxSpot}
        topLeft={
          <SolveModeLabel
            workspaceType={workspaceType}
            solvingMode={solvingMode}
            isMultipleSolvingMode={isMultipleSolvingMode}
          />
        }
        bottomLeft={
          flowWay !== 'NoFlowWay' &&
          formatMessage({
            id: getFlowWayTranslationKey(flowWay),
          })
        }
        bottomRight={
          hasProgress && (
            <CountdownTimer
              className="position-absolute"
              color="info"
              remainingTimeInSeconds={remainingTimeInSeconds}
              style={{ right: '1rem', bottom: '1rem' }}
            />
          )
        }
        actions={children}
      />
      {hasProgress && <ProgressBar percent={100 - progressInPercent} className="bg-info" />}
    </>
  );
}

export const QuotedTradable = connectQuotedTradeable(Component);

Component.displayName = 'QuotedTradable';
